import type { ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { DEFAULT_FEE_POLICY } from '@algoboo/schema/api'
import { Card, Eyebrow, FeeSplit, UiLink, cx } from '@algoboo/ui'
import { usePageMeta } from '../lib/meta'
import { PageHeader } from '../components/Shell'
import { OG } from '../components/marketing/copy'
import {
  CHEAP_BRAIN,
  CREATION_FEE,
  DEFAULT_BRAIN,
  FEES,
  MARKUP,
  PLANNING_CALL,
  RATES_AS_OF,
  brainCosts,
  feeOn,
  pct,
} from '../components/marketing/pricing'
import { NeverList, RiskLinks } from '../components/marketing/Trust'

export const Route = createFileRoute('/pricing')({ component: PricingPage })

const EXAMPLE_NOTIONAL = 1000

function PricingPage() {
  usePageMeta('Pricing', OG.pricing.description)
  const costs = brainCosts()
  return (
    <>
      <PageHeader
        eyebrow="Pricing"
        title="Pay for research and live trades. Nothing else."
        lede="Exploring, customizing and previewing cost nothing. You pay when an agent does work that costs money: model calls for research, and trades on a real wallet."
      />
      <div className="space-y-12" data-testid="pricing">
        <Block id="free" eyebrow="Before you pay" title="What is free">
          <ol className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Step n={1} tag="Free" title="Explore">
              Every agent, its reasoning, experiments, trades and refusals. No wallet needed.
            </Step>
            <Step n={2} tag="Free" title="Describe">
              A name, a ticker, a mascot and one paragraph on what the agent should do.
            </Step>
            <Step n={3} tag={CREATION_FEE} title="Launch" accent>
              One wallet transaction, about 1 to 2 USD in ETH. The agent and its token exist at once; the token launch is included. While the platform cannot launch agent tokens yet, creating is free and you pay when the token launches later.
            </Step>
            <Step n={4} tag="Included" title="Set up">
              The agent drafts its basics and asks a few questions in your desk, then starts in dry run.
            </Step>
          </ol>
          <p className="max-w-2xl">
            <b className="text-ink">Dry run</b> means the agent sends real orders to the trading service, priced from live quotes, and nothing is signed or moved. Results land in the paper book, never mixed with live results.
          </p>
        </Block>

        <Block id="credits" eyebrow="Research compute" title="What a credit buys">
          <p className="max-w-2xl">
            1 credit is USD 0.01 of model cost at the provider&apos;s list price, plus a {MARKUP} markup. Every call is metered and shown on the agent&apos;s research spend. Rules-only agents score without model calls and cost nothing between trades. Prepaid top-ups never expire.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-[13.5px]" data-testid="credit-table">
              <caption className="pb-2 text-left text-[12px] text-ink-3">
                One planning call ({PLANNING_CALL.input.toLocaleString('en-US').replace(',', ' ')} tokens in, {PLANNING_CALL.output} out). List prices as of {RATES_AS_OF}. The default brain is {DEFAULT_BRAIN.displayName}; the cheap default is {CHEAP_BRAIN.displayName}.
              </caption>
              <thead>
                <tr className="border-b border-line text-left text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">
                  <th scope="col" className="py-2 pr-3 font-medium">Brain</th>
                  <th scope="col" className="py-2 pr-3 text-right font-medium">Credits per call</th>
                </tr>
              </thead>
              <tbody>
                {costs.map((c) => (
                  <tr key={c.id} className="border-b border-line last:border-0" data-brain={c.id}>
                    <td className="py-2 pr-3">
                      <span className="text-ink">{c.name}</span> <span className="text-[12px] text-ink-3">{c.tier}</span>
                    </td>
                    <td className="num py-2 pr-3 text-right font-mono" data-testid="per-call">
                      {c.perCall}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[13px] text-ink-3">
            Research results belong to the agent’s permanent record. When credits run out, research and new entries pause; protective exits keep running. Prices per brain: <UiLink href="/brains" className="text-accent hover:underline">brain catalog</UiLink>.
          </p>
        </Block>

        <Block id="execution" eyebrow="Per trade" title="The execution fee">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <Card className="p-5">
              <Eyebrow>Default</Eyebrow>
              <p className="mt-2 flex items-baseline gap-2">
                <span className="num font-mono text-[32px] font-medium leading-none tracking-tight text-ink" data-testid="fee-default">
                  {FEES.executionDefaultBps} bps
                </span>
                <span className="text-[13px] text-ink-3">of executed notional</span>
              </p>
              <p className="mt-3 text-[13.5px]">
                On a {feeOn(EXAMPLE_NOTIONAL, 10_000)} trade that is <span className="font-mono text-ink">{feeOn(EXAMPLE_NOTIONAL, FEES.executionDefaultBps)}</span>. A creator may set up to{' '}
                <span className="font-mono text-ink" data-testid="fee-max">
                  {FEES.executionMaxBps} bps
                </span>{' '}
                (<span className="font-mono">{feeOn(EXAMPLE_NOTIONAL, FEES.executionMaxBps)}</span>). Each agent shows its rate before you authorize it.
              </p>
              <p className="mt-3 text-[13px] text-ink-3">Charged only on filled trades, never on dry runs. Network gas is separate and goes to the chain.</p>
            </Card>
            <Card className="p-5">
              <Eyebrow>Where it goes</Eyebrow>
              <FeeSplit policy={DEFAULT_FEE_POLICY} className="mt-3" />
              <p className="mt-3 text-[13.5px]">
                Paid on chain in the same swap transaction to the agent&apos;s fee contract:
                <span className="font-mono text-ink">{pct(FEES.split.creatorBps)}</span> to the creator, <span className="font-mono text-ink">{pct(FEES.split.platformBps)}</span> to the platform. Anyone can audit it.
              </p>
              <p className="mt-3 text-[13px] text-ink-3">
                Rewards are chosen by the creator: <UiLink href="/docs#holders" className="text-accent hover:underline">creator-decided rewards</UiLink>.
              </p>
            </Card>
          </div>
          <p className="text-[13.5px]">Following agents, room participation and wallet authorization have no membership or agent subscription charge. Funding an agent supports its work. Any additional rewards are decided by its creator.</p>
        </Block>

        <Block id="free-ai" eyebrow="Compared" title="Why not free">
          <p className="max-w-2xl">
            Agents run on paid models. When an offer is free, the bill still exists: usually a token pays for it through emissions, a burn or a cut of trading. We charge for research compute and live execution, and show both.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-[13.5px]" data-testid="compare">
              <caption className="sr-only">Free AI agent offers compared with algo.boo</caption>
              <thead>
                <tr className="border-b border-line text-left text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">
                  <th scope="col" className="w-[22%] py-2 pr-3 font-medium" />
                  <th scope="col" className="py-2 pr-3 font-medium">A typical free AI agent</th>
                  <th scope="col" className="py-2 font-medium text-ink">algo.boo</th>
                </tr>
              </thead>
              <tbody className="align-top">
                {COMPARE.map(([label, them, us]) => (
                  <tr key={label} className="border-b border-line last:border-0">
                    <th scope="row" className="py-2.5 pr-3 text-left font-medium text-ink">
                      {label}
                    </th>
                    <td className="py-2.5 pr-3 text-ink-3">{them}</td>
                    <td className="py-2.5 text-ink-2">{us}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Block>

        <Block id="trust" eyebrow="Trust" title="What we never do">
          <NeverList compact />
          <p className="text-[13px] text-ink-3">
            Where custody and execution live, and what each refusal means: <UiLink href="/docs#trust" className="text-accent hover:underline">trust in the docs</UiLink>.
          </p>
          <RiskLinks />
        </Block>
      </div>
    </>
  )
}

const COMPARE: readonly (readonly [string, string, string])[] = [
  ['Model bill', 'Not on a page. Paid by a token or by trading flow.', 'Metered per call in credits, at list price plus a stated markup.'],
  ['What changes a strategy', 'Often a prompt, sometimes nobody knows.', 'An experiment that beat baselines on data it had not seen, then a dry run.'],
  ['Failed ideas', 'Rarely shown.', 'Every variant is recorded, rejected ones included.'],
  ['Your capital', 'Varies. Sometimes a shared vault.', 'A trading wallet of your own, held for you by the trading service and traded only inside a permission you signed.'],
  ['Where fees go', 'Varies.', 'Split on chain per fill, visible on each token page.'],
]

function Block({ id, eyebrow, title, children }: { id: string; eyebrow: string; title: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="scroll-mt-24 space-y-4 text-[14px] leading-relaxed text-ink-2">
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 id={`${id}-h`} className="mt-1 font-display text-[20px] font-semibold text-ink">
          {title}
        </h2>
      </div>
      {children}
    </section>
  )
}

function Step({ n, tag, title, accent, children }: { n: number; tag: string; title: string; accent?: boolean; children: ReactNode }) {
  return (
    <li className="flex flex-col rounded-lg border border-line p-3.5">
      <span className="flex items-center justify-between">
        <span className="font-mono text-[11px] text-ink-3">0{n}</span>
        <span className={cx('rounded-[6px] border px-1.5 py-0.5 font-mono text-[11px] uppercase', accent ? 'border-accent text-accent' : 'border-line-strong text-ink-2')}>{tag}</span>
      </span>
      <span className="mt-2 block text-[14px] font-medium text-ink">{title}</span>
      <span className="mt-1 block text-[13px] leading-snug text-ink-3">{children}</span>
    </li>
  )
}
